import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "../Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, UserCog, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ROLES = ["company_admin", "team_admin", "employee", "super_admin"];

export default function AdminUsersPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", role: "all", is_active: "all" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.role !== "all") params.role = filters.role;
    if (filters.is_active !== "all") params.is_active = filters.is_active === "true";
    const { data } = await api.get("/admin/users", { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const impersonate = async (u) => {
    if (u.role === "super_admin") { toast.error("Cannot impersonate another super admin"); return; }
    if (!window.confirm(`Impersonate ${u.name} (${u.email})? This will be audit-logged.`)) return;
    try {
      await api.post(`/admin/impersonate/${u.user_id}`);
      toast.success(`Impersonating ${u.name}`);
      nav("/app");
      window.location.reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Super Admin</div>
          <h1 className="font-display text-4xl mt-1">Users</h1>
          <p className="text-sm text-[#4B5773] mt-2">Every user across every tenant.</p>
        </div>
      </div>

      <Card className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-1">
            <Search className="absolute left-2 top-3 h-4 w-4 text-slate-400"/>
            <Input value={filters.q} placeholder="Search name or email"
              data-testid="admin-users-search"
              onChange={(e)=>setFilters({...filters, q: e.target.value})}
              className="h-10 pl-8 rounded-md"/>
          </div>
          <Select value={filters.role} onValueChange={(v)=>setFilters({...filters, role: v})}>
            <SelectTrigger data-testid="admin-users-role-filter" className="h-10 rounded-md"><SelectValue placeholder="Role"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.is_active} onValueChange={(v)=>setFilters({...filters, is_active: v})}>
            <SelectTrigger data-testid="admin-users-active-filter" className="h-10 rounded-md"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#EEF3FB]/95 text-[10px] uppercase tracking-widest text-[#4B5773]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">Company</th>
                <th className="text-left px-3 py-3">Role</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Last login</th>
                <th className="text-left px-3 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(u => (
                <tr key={u.user_id} className="hover:bg-[#F6F9FE]" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-3 text-[#4B5773]">{u.email}</td>
                  <td className="px-3 text-[#4B5773]">{u.company_name || "-"}</td>
                  <td className="px-3 text-[#4B5773]">{u.role.replace("_", " ")}</td>
                  <td className="px-3">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${
                      u.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`}>
                      {u.is_active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-3 text-[#4B5773] text-xs">{u.last_login_at?.slice(0,10) || "-"}</td>
                  <td className="px-3 text-[#4B5773] text-xs">{u.created_at?.slice(0,10) || "-"}</td>
                  <td className="px-4 text-right">
                    {u.role !== "super_admin" && (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={()=>setEditing(u)}
                          data-testid={`admin-user-edit-${u.user_id}`}
                          className="text-[#0B1A36] hover:bg-[#EEF3FB]">
                          <Pencil className="h-4 w-4 mr-1"/>Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={()=>impersonate(u)}
                          data-testid={`admin-user-impersonate-${u.user_id}`}
                          className="text-[#1D4ED8] hover:bg-[#DBE7FE]">
                          <UserCog className="h-4 w-4 mr-1"/>Impersonate
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="py-8 text-center text-[#4B5773]">No users match the filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <UserEditDialog user={editing} onClose={()=>setEditing(null)} onSaved={load}/>
    </div>
  );
}


/**
 * Phase 2Z.ac - Super Admin user support edit.
 *
 * Soft, reversible support actions only:
 *   - Toggle is_active (deactivate / reactivate)
 *   - Correct email address (typo fix, wrong-email-at-signup)
 *
 * Requires a free-text `reason` plus a window.confirm step before
 * sending the PATCH. Stripe/billing/role/company/password fields are
 * not editable - the backend allow-list strictly enforces this and
 * the form does not expose them.
 *
 * Backend: PATCH /api/admin/users/{user_id} (allow-list: email,
 * is_active, reason). Audit row is written by the endpoint via
 * _audit("admin.user.updated", ...) so we do not need a separate
 * client-side log call.
 */
function UserEditDialog({ user, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setIsActive(user.is_active !== false);
      setReason("");
    }
  }, [user]);
  if (!user) return null;

  const emailChanged = email.trim().toLowerCase() !== (user.email || "").trim().toLowerCase();
  const activeChanged = isActive !== (user.is_active !== false);
  const anyChange = emailChanged || activeChanged;

  const submit = async () => {
    if (!anyChange) { onClose(); return; }
    if (!reason.trim()) { toast.error("Please add a reason for the change."); return; }

    const changes = [];
    if (emailChanged) changes.push(`email: ${user.email} -> ${email.trim()}`);
    if (activeChanged) changes.push(`is_active: ${user.is_active !== false} -> ${isActive}`);
    const ok = window.confirm(
      `Confirm changes to ${user.name} (${user.email}):\n\n` +
      changes.join("\n") +
      `\n\nReason: ${reason.trim()}\n\nThis is audit-logged.`
    );
    if (!ok) return;

    setSaving(true);
    try {
      const payload = { reason: reason.trim() };
      if (emailChanged) payload.email = email.trim();
      if (activeChanged) payload.is_active = isActive;
      await api.patch(`/admin/users/${user.user_id}`, payload);
      toast.success("Saved");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent fullscreenOnMobile className="max-w-lg" data-testid="admin-user-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit user: {user.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md bg-[#F6F9FE] border border-slate-200 px-3 py-2 text-xs text-[#4B5773]">
            Soft support actions only. To remove access, deactivate (reversible). To fix a typo, correct the email. Passwords, roles, company and billing fields are not editable here.
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5773] uppercase tracking-widest mb-1">Email address</label>
            <Input
              type="email"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              data-testid="admin-user-edit-email"
              className="h-10 rounded-md"
            />
            <div className="text-[11px] text-[#4B5773] mt-1">Used for login and product emails. Validated and checked for duplicates.</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5773] uppercase tracking-widest mb-1">Account status</label>
            <Select value={isActive ? "true" : "false"} onValueChange={(v)=>setIsActive(v === "true")}>
              <SelectTrigger data-testid="admin-user-edit-status" className="h-10 rounded-md"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active (can log in, receives product emails)</SelectItem>
                <SelectItem value="false">Inactive (cannot log in, skipped by notifications)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-[#4B5773] mt-1">No rota, leave, history or billing data is deleted. Action is reversible.</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4B5773] uppercase tracking-widest mb-1">Reason / support context</label>
            <Input
              value={reason}
              onChange={(e)=>setReason(e.target.value)}
              placeholder="e.g. User left company. Support ticket #1234"
              data-testid="admin-user-edit-reason"
              className="h-10 rounded-md"
            />
            <div className="text-[11px] text-[#4B5773] mt-1">Required. Saved to the audit log for every change.</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="admin-user-edit-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !anyChange}
            data-testid="admin-user-edit-save"
            className="bg-[#1D4ED8] hover:bg-[#1746B4] text-white"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
